import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const PortalContext = createContext(null);

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

// ── Chaves do localStorage ──────────────────────────────────
const KEY_TOKEN  = 'portal_token';
const KEY_USER   = 'portal_user';
const KEY_PLANOS = 'portal_planos';
const KEY_CPF    = 'portal_cpf_enc'; // CPF cifrado (AES-GCM)

// ── Criptografia AES-GCM 256-bit via Web Crypto API ─────────
// Chave derivada do JWT via HKDF-SHA-256 — sem bibliotecas externas

const SALT = new TextEncoder().encode('aytech-portal-salt-v1');
const INFO = new TextEncoder().encode('cpf-field');

async function deriveKey(jwt) {
  const raw = new TextEncoder().encode(jwt);
  const base = await crypto.subtle.importKey('raw', raw, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: SALT, info: INFO },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

async function encryptCPF(cpf, jwt) {
  try {
    const key = await deriveKey(jwt);
    const iv  = crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV
    const enc = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      new TextEncoder().encode(cpf)
    );
    // Concatena IV (12 bytes) + ciphertext e codifica em base64
    const buf = new Uint8Array(12 + enc.byteLength);
    buf.set(iv);
    buf.set(new Uint8Array(enc), 12);
    return btoa(String.fromCharCode(...buf));
  } catch {
    return null;
  }
}

async function decryptCPF(stored, jwt) {
  try {
    if (!stored || !jwt) return null;
    const key = await deriveKey(jwt);
    const buf = Uint8Array.from(atob(stored), c => c.charCodeAt(0));
    const iv   = buf.slice(0, 12);
    const data = buf.slice(12);
    const dec  = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, data);
    return new TextDecoder().decode(dec);
  } catch {
    return null; // Key errada ou dado corrompido — não expõe nada
  }
}

// ── Helpers de cache (user e planos — não sensíveis) ─────────
function saveCache(user, planos) {
  try {
    localStorage.setItem(KEY_USER,   JSON.stringify(user));
    localStorage.setItem(KEY_PLANOS, JSON.stringify(planos));
  } catch { /* storage cheio */ }
}

function loadCache() {
  try {
    const user   = JSON.parse(localStorage.getItem(KEY_USER)   || 'null');
    const planos = JSON.parse(localStorage.getItem(KEY_PLANOS) || '[]');
    return { user, planos };
  } catch {
    return { user: null, planos: [] };
  }
}

function clearCache() {
  [KEY_TOKEN, KEY_USER, KEY_PLANOS, KEY_CPF].forEach(k => localStorage.removeItem(k));
}

// ─────────────────────────────────────────────────────────────

export const PortalProvider = ({ children }) => {
  // Estado inicializado instantaneamente do cache — sem flicker no reload
  const cached = loadCache();
  const [user,   setUser]   = useState(cached.user);
  const [planos, setPlanos] = useState(cached.planos);
  const [token,  setToken]  = useState(() => localStorage.getItem(KEY_TOKEN));
  const [cpf,    setCpf]    = useState(null); // descriptografado em memória
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  // Descriptografa CPF armazenado ao montar (async, só em memória)
  useEffect(() => {
    const tkn = localStorage.getItem(KEY_TOKEN);
    const enc = localStorage.getItem(KEY_CPF);
    if (tkn && enc) {
      decryptCPF(enc, tkn).then(plain => {
        if (plain) setCpf(plain);
      });
    }
  }, []);

  // Salva cache de perfil sempre que dados mudam
  useEffect(() => {
    if (user && planos) saveCache(user, planos);
  }, [user, planos]);

  const logoutRef = useRef(null);

  const logout = useCallback(() => {
    clearCache();
    setToken(null);
    setUser(null);
    setPlanos([]);
    setCpf(null);
    setError(null);
  }, []);

  logoutRef.current = logout;

  const fetchDashboard = useCallback(async (tkn, { silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const res = await fetch(`${API_BASE}/api/portal/dashboard`, {
        headers: {
          Authorization: `Bearer ${tkn}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
        },
        cache: 'no-store',
      });

      if (res.status === 401) {
        // JWT expirado ou inválido → desloga
        logoutRef.current();
        return;
      }
      if (!res.ok) return; // erro de rede — mantém cache

      const data = await res.json();
      setUser(data.assinante);
      setPlanos(data.planos);
      saveCache(data.assinante, data.planos);
    } catch {
      // Sem rede — mantém cache, não desloga
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Na montagem: cache → render imediato + refresh silencioso em background
  useEffect(() => {
    if (!token) return;
    if (user) {
      fetchDashboard(token, { silent: true });
    } else {
      fetchDashboard(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loginComDocumento = async (rawDocumento) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/portal/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documento: rawDocumento })
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || 'CPF/CNPJ não encontrado.');
        return false;
      }

      // Persiste token e perfil
      localStorage.setItem(KEY_TOKEN, data.token);
      setToken(data.token);
      setUser(data.assinante);
      setPlanos(data.planos);
      saveCache(data.assinante, data.planos);

      // Criptografa o documento com AES-GCM usando o JWT como material da chave
      const enc = await encryptCPF(rawDocumento, data.token);
      if (enc) localStorage.setItem(KEY_CPF, enc);

      setCpf(rawDocumento); // mantém em memória sem re-criptografar
      return true;
    } catch {
      setError('Erro ao conectar. Tente novamente.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // ── PIX (Mercado Pago) ──────────────────────────────────
  const criarPagamentoPix = async (planoId) => {
    if (!token) return { success: false, message: 'Não autenticado.' };
    try {
      const res = await fetch(`${API_BASE}/api/portal/pix/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ plano_id: planoId }),
      });
      return await res.json();
    } catch {
      return { success: false, message: 'Erro ao conectar ao servidor.' };
    }
  };

  const verificarStatusPix = async (pagamentoId) => {
    if (!token) return { success: false, message: 'Não autenticado.' };
    try {
      const res = await fetch(`${API_BASE}/api/portal/pix/status/${pagamentoId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return await res.json();
    } catch {
      return { success: false, message: 'Erro ao verificar status.' };
    }
  };

  const refreshDashboard = useCallback(() => {
    if (token) fetchDashboard(token, { silent: true });
  }, [token, fetchDashboard]);

  // ── Computed ────────────────────────────────────────────
  const totalFatura = planos
    .filter(p => p.ativo)
    .reduce((acc, p) => acc + parseFloat(p.valor_mensal || 0), 0);

  const proximoVencimento = planos
    .filter(p => p.ativo && p.data_vencimento)
    .map(p => p.data_vencimento)
    .sort()[0] || null;

  return (
    <PortalContext.Provider value={{
      user,
      planos,
      token,
      cpf,
      loading,
      error,
      isLoggedIn: !!user,
      totalFatura,
      proximoVencimento,
      loginComDocumento,
      logout,
      setError,
      criarPagamentoPix,
      verificarStatusPix,
      refreshDashboard,
    }}>
      {children}
    </PortalContext.Provider>
  );
};

export const usePortal = () => {
  const ctx = useContext(PortalContext);
  if (!ctx) throw new Error('usePortal deve ser usado dentro de PortalProvider');
  return ctx;
};
